import { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Breadcrumbs,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Link,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
} from '@mui/material';
import FolderIcon from '@mui/icons-material/Folder';
import HomeIcon from '@mui/icons-material/Home';

import { requireAuth, requirePartner } from '@src/providers/dataProvider/shared';

import { fetchSftpFolderContents } from './fetchSftpFolderContents';

interface MoveDialogProps {
  open: boolean;
  entry: string;
  itemNames: string[];
  /** Full paths of any selected folders, so a folder can't be moved into itself or one of its own descendants. */
  excludedPaths: string[];
  /** The items' current parent folder — moving to this same folder is a no-op. */
  currentPath?: string;
  onClose: () => void;
  onSubmit: (destinationPath: string | undefined) => Promise<void>;
}

const joinPath = (parentPath: string | undefined, name: string): string =>
  parentPath ? `${parentPath}/${name}` : name;

const MoveDialog = ({ open, entry, itemNames, excludedPaths, currentPath, onClose, onSubmit }: MoveDialogProps) => {
  const [browsePath, setBrowsePath] = useState<string | undefined>(undefined);
  const [folders, setFolders] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (open) setBrowsePath(undefined);
  }, [open]);

  const load = useCallback(async (path: string | undefined) => {
    setIsLoading(true);
    setError(null);
    try {
      const token = requireAuth();
      const partnerCode = requirePartner().code;
      const contents = await fetchSftpFolderContents({ token, partnerCode, entry, path });
      setFolders(contents.folders);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load folders.');
    } finally {
      setIsLoading(false);
    }
  }, [entry]);

  useEffect(() => {
    if (open) void load(browsePath);
  }, [open, browsePath, load]);

  const isExcluded = (path: string) =>
    excludedPaths.some(excluded => path === excluded || path.startsWith(`${excluded}/`));

  const segments: { label: string, path: string | undefined }[] = [{ label: 'Home', path: undefined }];
  const parts = (browsePath ?? '').split('/').filter(Boolean);
  parts.forEach((part, index) => {
    segments.push({ label: part, path: parts.slice(0, index + 1).join('/') });
  });

  const isNoOp = browsePath === currentPath;

  const handleSubmit = async () => {
    setIsSubmitting(true);
    try {
      await onSubmit(browsePath);
      onClose();
    } finally {
      setIsSubmitting(false);
    }
  };

  const title = itemNames.length === 1 ? `Move "${itemNames[0]}"` : `Move ${itemNames.length} items`;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ mb: 1.5 }}>Choose a destination folder.</DialogContentText>

        <Breadcrumbs sx={{ mb: 1.5 }}>
          {segments.map((segment, index) => {
            const isLast = index === segments.length - 1;
            return isLast ?
              (
                <Typography key={segment.label} color="text.secondary" sx={{ fontSize: 14 }}>
                  {segment.label}
                </Typography>
              ) :
              (
                <Link
                  key={segment.label}
                  component="button"
                  underline="hover"
                  sx={{ fontSize: 14, display: 'flex', alignItems: 'center', gap: 0.5 }}
                  onClick={() => setBrowsePath(segment.path)}
                >
                  {index === 0 && <HomeIcon sx={{ fontSize: 16 }} />}
                  {segment.label}
                </Link>
              );
          })}
        </Breadcrumbs>

        {error && <Typography color="error" sx={{ fontSize: 13, mb: 1.5 }}>{error}</Typography>}

        {isLoading ?
          (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
              <CircularProgress size={24} />
            </Box>
          ) :
          (
            <List dense sx={{ maxHeight: 240, overflow: 'auto', border: theme => `1px solid ${theme.palette.divider}`, borderRadius: 1 }}>
              {folders.length === 0 && (
                <Typography color="text.secondary" sx={{ fontSize: 13, p: 2, textAlign: 'center' }}>
                  No sub-folders here.
                </Typography>
              )}
              {folders.map((name) => {
                const folderPath = joinPath(browsePath, name);
                const disabled = isExcluded(folderPath);
                return (
                  <ListItemButton key={name} disabled={disabled} onClick={() => setBrowsePath(folderPath)}>
                    <ListItemIcon sx={{ minWidth: 32 }}>
                      <FolderIcon fontSize="small" sx={{ color: disabled ? 'text.disabled' : 'primary.main' }} />
                    </ListItemIcon>
                    <ListItemText primary={name} />
                  </ListItemButton>
                );
              })}
            </List>
          )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          color="success"
          disabled={isSubmitting || isNoOp}
          onClick={() => { void handleSubmit(); }}
        >
          Move Here
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default MoveDialog;
